import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Camera, Square, Search, PackagePlus, Trash2, Printer } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { openPdf } from "../lib/api";
import PhotoAttachments from "../components/PhotoAttachments";

const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function Scanner() {
  const scannerRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [lastCode, setLastCode] = useState("");
  const [lookup, setLookup] = useState(null);
  const [manualCode, setManualCode] = useState("");
  const navigate = useNavigate();

  // ---- Wareneingang-Modus ----
  const [receiveMode, setReceiveMode] = useState(false);
  const [receiveShelf, setReceiveShelf] = useState("");
  const [pending, setPending] = useState(null); // {item, code}
  const [form, setForm] = useState({ quantity: "", charge_number: "", mhd: todayPlus(180), location: "" });
  const [receivedLog, setReceivedLog] = useState([]); // recent quick-receive results
  const [autoLabel, setAutoLabel] = useState(true);  // auto-open batch label PDF after receive

  const start = async () => {
    try {
      const el = document.getElementById("qr-reader");
      if (!el) return;
      const cams = await Html5Qrcode.getCameras();
      if (!cams || cams.length === 0) return toast.error("Keine Kamera gefunden");
      const scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 12, qrbox: { width: 260, height: 220 } },
        async (decoded) => {
          setLastCode(decoded);
          await handleCode(decoded);
        },
        () => {}
      );
      setRunning(true);
    } catch (err) {
      toast.error("Scanner-Fehler: " + err.message);
    }
  };

  const stop = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
        scannerRef.current = null;
      }
    } catch (_e) { /* ignore */ }
    setRunning(false);
  };

  useEffect(() => () => { stop(); }, []);

  const parseShelfFromCode = (code) => {
    try {
      if (code.startsWith("http")) {
        const u = new URL(code);
        if (u.pathname.includes("/regale")) return u.searchParams.get("location");
      }
    } catch (_e) { /* ignore */ }
    if (code.startsWith("POPCRN|SHELF:")) return code.slice("POPCRN|SHELF:".length);
    return null;
  };

  const handleCode = async (code) => {
    // Shelf QR ------------------------------------------------------
    const shelfLoc = parseShelfFromCode(code);
    if (shelfLoc) {
      if (receiveMode) {
        setReceiveShelf(shelfLoc);
        setForm((f) => ({ ...f, location: shelfLoc }));
        toast.success(`Regal ${shelfLoc} für Wareneingang gesetzt`);
        return;
      }
      toast.success(`Regal ${shelfLoc} – Chargen werden geladen…`);
      await stop();
      navigate(`/regale?location=${encodeURIComponent(shelfLoc)}`);
      return;
    }

    // Extract sku/charge from POPCRN payload
    let charge = null;
    let sku = null;
    if (code.startsWith("POPCRN|")) {
      const parts = code.split("|");
      sku = parts[1];
      charge = parts[2];
    } else {
      charge = code;
    }

    // In Wareneingang-Modus: nur Item anhand SKU/Barcode auflösen und Form öffnen
    if (receiveMode) {
      try {
        const { data } = await api.get("/items", { params: { q: sku || code } });
        if (data && data.length > 0) {
          const item = data[0];
          setPending({ item, code });
          setForm({
            quantity: "",
            charge_number: `WE-${new Date().toISOString().slice(0, 10).replace(/-/g, "")}-${Math.floor(Math.random() * 900 + 100)}`,
            mhd: todayPlus(item.default_shelf_life_days || 180),
            location: receiveShelf || item.warehouse_location || "",
          });
          toast.success(`Artikel: ${item.name}`);
          return;
        }
      } catch (_e) { /* ignore */ }
      toast.error("Kein Artikel gefunden für: " + code);
      return;
    }

    // Standard-Modus: batch suchen
    if (charge) {
      try {
        const { data } = await api.get("/batches", { params: { charge } });
        if (data && data.length > 0) {
          setLookup({ kind: "batch", data: data[0] });
          toast.success(`Charge ${data[0].charge_number} · ${data[0].item_name}`);
          return;
        }
      } catch (_e) { /* ignore */ }
    }

    try {
      const { data } = await api.get("/items", { params: { q: sku || code } });
      if (data && data.length > 0) {
        const item = data[0];
        // Bundle-EAN Erkennung: fuzzy check ob gescannter Code eine Bundle-EAN
        // des gefundenen Artikels ist. Zeigt dann Bundle-Label + Preis.
        let bundle_match = null;
        if (item.bundles) {
          for (const code of ["13er", "40er", "palette"]) {
            const b = item.bundles[code];
            if (b && b.enabled && b.ean && b.ean === (sku || code)) {
              bundle_match = { code, ...b };
              break;
            }
          }
        }
        // Fetch FEFO suggestion so scanning an item highlights the next batch
        let fefo = null;
        try {
          const r = await api.get(`/items/${item.id}/fefo`, { params: { qty: 1 } });
          const picks = r.data?.batches || [];
          if (picks.length > 0) fefo = picks[0];
        } catch (_e) { /* ignore */ }
        setLookup({ kind: "item", data: item, fefo, bundle_match });
        const bundleLabel = bundle_match ? ` · ${bundle_match.code} Bundle` : "";
        toast.success(`Artikel ${item.sku} · ${item.name}${bundleLabel}${fefo ? ` · FEFO: ${fefo.charge_number}` : ""}`);
        return;
      }
    } catch (_e) { /* ignore */ }

    setLookup({ kind: "unknown", data: { code } });
    toast.warning("Kein Treffer für: " + code);
  };

  const submitReceive = async (e) => {
    e.preventDefault();
    if (!pending?.item) return;
    try {
      const { data } = await api.post("/warehouse/quick-receive", {
        code: pending.item.sku,
        quantity: parseFloat(form.quantity),
        charge_number: form.charge_number.trim(),
        mhd: form.mhd,
        location: form.location || null,
      });
      toast.success(`✓ ${data.quantity} ${data.item.unit} · ${data.item.name} → Regal ${data.location || "—"}`);
      setReceivedLog((log) => [{ ...data, at: new Date() }, ...log].slice(0, 10));
      setPending(null);
      // Auto-open the batch label PDF so warehouse can print & stick it right away
      if (autoLabel && data.batch_id) {
        try {
          await openPdf(`/batches/${data.batch_id}/label.pdf`);
        } catch (err) {
          toast.warning("Etikett konnte nicht geöffnet werden");
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Wareneingang fehlgeschlagen");
    }
  };

  const printLabel = async (batchId) => {
    try {
      await openPdf(`/batches/${batchId}/label.pdf`);
    } catch (err) {
      toast.error("Etikett konnte nicht geöffnet werden");
    }
  };

  return (
    <div data-testid="page-scanner">
      <PageHeader
        eyebrow="Barcode"
        title="Scanner"
        description="Nutze die Kamera des Geräts, um Chargen-Etiketten, EAN- oder POPCRN QR-Codes zu erfassen."
        actions={
          <div className="flex items-center gap-3 bg-white border border-[var(--border)] rounded-full px-4 py-2">
            <PackagePlus size={16} className={receiveMode ? "text-emerald-600" : "text-[var(--text-muted)]"} />
            <Label htmlFor="receive-mode" className="text-sm font-semibold cursor-pointer">Wareneingang-Modus</Label>
            <Switch
              id="receive-mode"
              data-testid="switch-receive-mode"
              checked={receiveMode}
              onCheckedChange={(v) => { setReceiveMode(v); setPending(null); }}
            />
          </div>
        }
      />

      {receiveMode && (
        <div className="mb-6 rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-4" data-testid="receive-mode-banner">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="font-display font-black text-lg text-emerald-900">Wareneingang aktiv</div>
              <div className="text-xs text-emerald-800">
                1) Regal-QR scannen (optional) → 2) Artikel-Barcode scannen → 3) Menge & MHD prüfen → Speichern.
              </div>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 text-xs font-semibold text-emerald-900 cursor-pointer">
                <Checkbox
                  checked={autoLabel}
                  onCheckedChange={(v) => setAutoLabel(!!v)}
                  data-testid="checkbox-auto-label"
                />
                <Printer size={14} /> Etikett automatisch öffnen
              </label>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-emerald-900">Aktives Regal:</span>
                <Input
                  value={receiveShelf}
                  onChange={(e) => setReceiveShelf(e.target.value)}
                  placeholder="z.B. A-01-03"
                  data-testid="input-active-shelf"
                  className="rounded-xl w-40 h-9 text-sm font-mono"
                />
                {receiveShelf && (
                  <button
                    onClick={() => { setReceiveShelf(""); toast.info("Regal zurückgesetzt"); }}
                    data-testid="btn-clear-shelf"
                    className="text-emerald-700 hover:text-red-600"
                    title="Regal zurücksetzen"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[var(--border)] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">Live Kamera</div>
            {running ? (
              <Button onClick={stop} data-testid="btn-stop-scanner" className="rounded-full" variant="outline"><Square size={14} className="mr-1" /> Stop</Button>
            ) : (
              <Button onClick={start} data-testid="btn-start-scanner" className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold"><Camera size={14} className="mr-1" /> Kamera starten</Button>
            )}
          </div>
          <div id="qr-reader" className="rounded-xl overflow-hidden bg-black min-h-[280px]" />
          {lastCode && <div className="mt-3 text-xs font-mono text-[var(--text-muted)]">Zuletzt: {lastCode}</div>}

          <div className="mt-6">
            <div className="overline mb-2">Oder manuell</div>
            <div className="flex gap-2">
              <Input placeholder="Chargen-Nr. oder SKU / EAN" value={manualCode} onChange={(e) => setManualCode(e.target.value)} data-testid="input-scan-manual" className="rounded-xl" />
              <Button onClick={() => manualCode && handleCode(manualCode)} data-testid="btn-scan-manual" className="rounded-full bg-black text-white hover:bg-neutral-800"><Search size={14} className="mr-1" /> Suchen</Button>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[var(--border)] rounded-2xl p-6" data-testid="scanner-result">
          {/* Wareneingang-Formular hat Vorrang, wenn ein Artikel gescannt wurde */}
          {receiveMode && pending && (
            <form onSubmit={submitReceive} data-testid="quick-receive-form">
              <div className="overline mb-2">Wareneingang buchen</div>
              <h3 className="font-display font-black text-2xl mb-1">{pending.item.name}</h3>
              <div className="text-sm text-[var(--text-muted)] mb-4">{pending.item.sku} · {pending.item.type}</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Menge *</Label>
                  <Input required type="number" step="0.01" min="0.01" data-testid="input-receive-qty" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="rounded-xl" />
                </div>
                <div>
                  <Label>Einheit</Label>
                  <Input disabled value={pending.item.unit} className="rounded-xl opacity-60" />
                </div>
                <div className="col-span-2">
                  <Label>Charge-Nr *</Label>
                  <Input required data-testid="input-receive-charge" value={form.charge_number} onChange={(e) => setForm({ ...form, charge_number: e.target.value })} className="rounded-xl font-mono" />
                </div>
                <div>
                  <Label>MHD *</Label>
                  <Input required type="date" data-testid="input-receive-mhd" value={form.mhd} onChange={(e) => setForm({ ...form, mhd: e.target.value })} className="rounded-xl" />
                </div>
                <div>
                  <Label>Regal</Label>
                  <Input placeholder="A-01-03" data-testid="input-receive-shelf" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-xl font-mono" />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button type="submit" data-testid="btn-submit-receive" className="flex-1 rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">Einbuchen</Button>
                <Button type="button" variant="outline" className="rounded-full" onClick={() => setPending(null)} data-testid="btn-cancel-receive">Abbrechen</Button>
              </div>
            </form>
          )}

          {receiveMode && !pending && (
            <div>
              <div className="overline mb-2">Nächster Scan</div>
              <div className="text-sm text-[var(--text-muted)]">
                Scanne einen Artikel-Barcode oder SKU. Optional: Regal-QR vorher scannen, dann werden alle folgenden Artikel diesem Regal zugewiesen.
              </div>
              {receivedLog.length > 0 && (
                <div className="mt-6">
                  <div className="overline mb-2">Zuletzt eingebucht</div>
                  <ul className="divide-y divide-[var(--border)]" data-testid="received-log">
                    {receivedLog.map((r) => (
                      <li key={r.batch_id} className="py-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{r.item.name}</div>
                            <div className="text-xs font-mono text-[var(--text-muted)]">
                              {r.charge_number} · MHD {r.mhd} {r.location ? `· ${r.location}` : ""}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <div className="text-right">
                              <div className="font-mono text-sm font-semibold">{r.quantity} {r.item.unit}</div>
                              <div className="text-[10px] text-[var(--text-muted)]">{r.at.toLocaleTimeString("de-DE")}</div>
                            </div>
                            <button
                              onClick={() => printLabel(r.batch_id)}
                              data-testid={`btn-reprint-${r.batch_id}`}
                              title="Etikett erneut öffnen"
                              className="h-8 w-8 flex items-center justify-center rounded-full bg-[var(--pop-yellow)] hover:bg-[var(--pop-yellow-dark)] text-black"
                            >
                              <Printer size={14} />
                            </button>
                          </div>
                        </div>
                        <PhotoAttachments refType="receive" refId={r.batch_id}
                                          label="Fotos zu diesem Wareneingang" compact />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {!receiveMode && (
            <>
              <div className="overline mb-2">Treffer</div>
              {!lookup && <div className="text-sm text-[var(--text-muted)]">Scanne einen Code, um Details zu sehen.</div>}
              {lookup?.kind === "batch" && (
                <div>
                  <h3 className="font-display font-black text-2xl mb-1">{lookup.data.item_name}</h3>
                  <div className="text-sm text-[var(--text-muted)]">{lookup.data.item_sku}</div>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div><div className="overline">Charge</div><div className="font-mono">{lookup.data.charge_number}</div></div>
                    <div><div className="overline">MHD</div><div className="font-mono">{lookup.data.mhd}</div></div>
                    <div><div className="overline">Bestand</div><div className="font-mono">{lookup.data.current_stock} {lookup.data.item_unit}</div></div>
                    <div><div className="overline">Qualität</div><div className="font-mono">{lookup.data.quality_status}</div></div>
                  </div>
                </div>
              )}
              {lookup?.kind === "item" && (
                <div>
                  {lookup.bundle_match && (
                    <div className="mb-3 bg-[var(--pop-pink)] text-white rounded-xl px-4 py-2 inline-flex items-center gap-2" data-testid="scanner-bundle-badge">
                      <span className="text-lg font-bold">📦 {lookup.bundle_match.code === "13er" ? "13er Display" : lookup.bundle_match.code === "40er" ? "40er Display" : "Palette"}</span>
                      {lookup.bundle_match.price != null && <span className="font-mono">{Number(lookup.bundle_match.price).toFixed(2)} €</span>}
                    </div>
                  )}
                  <h3 className="font-display font-black text-2xl mb-1">{lookup.data.name}</h3>
                  <div className="text-sm text-[var(--text-muted)]">{lookup.data.sku} · {lookup.data.type}</div>
                  <div className="mt-3 text-sm">Einheit: <span className="font-mono">{lookup.data.unit}</span></div>
                  {lookup.data.barcode && <div className="text-sm">Barcode: <span className="font-mono">{lookup.data.barcode}</span></div>}
                  {lookup.bundle_match && lookup.bundle_match.ean && (
                    <div className="text-sm">Bundle-EAN: <span className="font-mono">{lookup.bundle_match.ean}</span></div>
                  )}
                  {lookup.fefo && (
                    <div className="mt-4 border-2 border-[var(--pop-pink)] bg-pink-50 rounded-xl p-3" data-testid="scanner-fefo-suggestion">
                      <div className="overline text-[var(--pop-pink)] mb-1">FEFO-Vorschlag</div>
                      <div className="font-mono font-bold text-lg">{lookup.fefo.charge_number}</div>
                      <div className="text-xs text-[var(--text-muted)] mt-1">
                        MHD <span className="font-mono">{lookup.fefo.mhd}</span>
                        <> · Regal <span className="font-mono">{lookup.fefo.location || "unbekannt"}</span></>
                        <> · verfügbar <span className="font-mono">{lookup.fefo.available}</span></>
                      </div>
                      {lookup.fefo.selection_reason && (
                        <div className="text-[11px] text-[var(--pop-pink)] font-semibold mt-1" data-testid="scanner-fefo-reason">
                          {lookup.fefo.selection_reason}
                        </div>
                      )}
                    </div>
                  )}
                  {!lookup.fefo && (
                    <div className="mt-4 text-xs text-orange-600 font-semibold" data-testid="scanner-fefo-empty">
                      Keine freigegebene Charge verfügbar (FEFO).
                    </div>
                  )}
                </div>
              )}
              {lookup?.kind === "unknown" && (
                <div className="text-sm text-red-700">Kein Treffer für: <span className="font-mono">{lookup.data.code}</span></div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
